import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { BACKEND_NODE_URL } from '../config/constants';
import './EditCRMProjectModalNew.css';
import AppIcon from './icons/AppIcon';

/**
 * Modal de edição para projetos CRM com abas
 * Abas: Informações Gerais | Conexão | Formulário | Agentes | Configurações
 */
export default function EditCRMProjectModalNew({ isOpen, onClose, project, onSave }) {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Estados do formulário
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'work',
    color: '#44e1ff',
    background_image: '',
    connection_id: '',
    useForm: false,
    formFields: [],
    agent_ids: [],
    allow_comments: true,
    allow_attachments: true,
    allow_subtasks: true,
    require_due_date: false,
    auto_archive_completed: false,
    notifications_enabled: true
  });

  // Estados auxiliares
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [agents, setAgents] = useState([]);
  const [connectionGroups, setConnectionGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Carregar dados ao abrir
  useEffect(() => {
    if (isOpen && project) {
      loadData();
    }
  }, [isOpen, project]);

  const loadData = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data'));

      // Carregar dados do projeto
      setFormData({
        name: project.name || '',
        description: project.description || '',
        icon: project.icon || 'work',
        color: project.color || '#44e1ff',
        background_image: project.background_image || '',
        connection_id: project.settings?.crm?.connection_id || '',
        useForm: project.settings?.crm?.useForm || false,
        formFields: project.settings?.crm?.formFields || [],
        agent_ids: project.settings?.crm?.agent_ids || [],
        allow_comments: project.settings?.allow_comments ?? true,
        allow_attachments: project.settings?.allow_attachments ?? true,
        allow_subtasks: project.settings?.allow_subtasks ?? true,
        require_due_date: project.settings?.require_due_date ?? false,
        auto_archive_completed: project.settings?.auto_archive_completed ?? false,
        notifications_enabled: project.settings?.notifications_enabled ?? true
      });

      // Carregar conexões
      const { data: connectionsData, error: connError } = await supabase
        .from('connections')
        .select('*')
        .eq('company_id', user.company_id)
        .eq('status', 'CONECTADO')
        .order('name');

      if (connError) console.error('Erro ao carregar conexões:', connError);
      setConnections(connectionsData || []);

      // Se tem conexão, carregar dados dela
      if (project.settings?.crm?.connection_id) {
        const conn = connectionsData?.find(c => c.id === project.settings.crm.connection_id);
        setSelectedConnection(conn || null);
      }

      // Carregar agentes
      const { data: agentsData, error: agentsError } = await supabase
        .from('agentes')
        .select('*')
        .eq('empresa_id', user.company_id)
        .order('nome');

      if (agentsError) console.error('Erro ao carregar agentes:', agentsError);
      setAgents(agentsData || []);

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConnectionGroups = async () => {
    if (!formData.connection_id) return;
    
    setLoadingGroups(true);
    try {
      const response = await fetch(
        `${BACKEND_NODE_URL}/connections/groups/${formData.connection_id}?force=true`,
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.ok) {
        const data = await response.json();
        setConnectionGroups(data.groups || []);
      }
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleConnectionChange = (connectionId) => {
    const conn = connections.find(c => c.id === connectionId);
    setSelectedConnection(conn || null);
    setFormData(prev => ({ 
      ...prev, 
      connection_id: connectionId,
      agent_ids: [] // Limpar agentes ao mudar conexão
    }));
    setConnectionGroups([]);
  };

  const handleAgentToggle = (agentId) => {
    setFormData(prev => {
      const isSelected = prev.agent_ids.includes(agentId);
      const agent = agents.find(a => a.id === agentId);
      
      // Verificar se o agente pertence à mesma conexão
      if (!isSelected && agent?.conexao_id && agent.conexao_id !== prev.connection_id) {
        return prev; // Não permite selecionar agente de outra conexão
      }
      
      return {
        ...prev,
        agent_ids: isSelected 
          ? prev.agent_ids.filter(id => id !== agentId)
          : [...prev.agent_ids, agentId]
      };
    });
  };

  // Formulário: adicionar campo
  const addFormField = () => {
    const newField = {
      id: Date.now(),
      name: '',
      icon: 'label',
      required: false
    };
    setFormData(prev => ({
      ...prev,
      formFields: [...prev.formFields, newField]
    }));
  };

  // Formulário: remover campo
  const removeFormField = (fieldId) => {
    setFormData(prev => ({
      ...prev,
      formFields: prev.formFields.filter(f => f.id !== fieldId)
    }));
  };

  // Formulário: atualizar campo
  const updateFormField = (fieldId, updates) => {
    setFormData(prev => ({
      ...prev,
      formFields: prev.formFields.map(f => 
        f.id === fieldId ? { ...f, ...updates } : f
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Nome do projeto é obrigatório');
      return;
    }

    try {
      setSaving(true);

      // Atualizar projeto
      const { error: projectError } = await supabase
        .from('projects')
        .update({
          name: formData.name,
          description: formData.description || null,
          icon: formData.icon,
          color: formData.color,
          background_image: formData.background_image || null,
          settings: {
            ...project.settings,
            crm: {
              ...project.settings?.crm,
              connection_id: formData.connection_id || null,
              useForm: formData.useForm,
              formFields: formData.formFields,
              agent_ids: formData.agent_ids
            },
            allow_comments: formData.allow_comments,
            allow_attachments: formData.allow_attachments,
            allow_subtasks: formData.allow_subtasks,
            require_due_date: formData.require_due_date,
            auto_archive_completed: formData.auto_archive_completed,
            notifications_enabled: formData.notifications_enabled
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', project.id);

      if (projectError) throw projectError;

      alert('✅ Projeto atualizado com sucesso!');
      onSave();
      onClose();
    } catch (error) {
      console.error('Erro ao salvar projeto:', error);
      alert('Erro ao salvar projeto: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const icons = [
    'work', 'business_center', 'store', 'shopping_cart', 'local_mall',
    'trending_up', 'analytics', 'assessment', 'bar_chart', 'pie_chart',
    'groups', 'person', 'contacts', 'phone', 'email',
    'campaign', 'rocket_launch', 'star', 'favorite', 'flag'
  ];

  const colors = [
    '#44e1ff', '#b43bea', '#ff6b6b', '#4ecdc4', '#45b7d1',
    '#f7b731', '#5f27cd', '#00d2d3', '#ff9ff3', '#54a0ff',
    '#48dbfb', '#1dd1a1', '#feca57', '#ee5a6f', '#c44569'
  ];

  const formFieldIcons = [
    'label', 'person', 'email', 'phone', 'business', 'location_on',
    'interests', 'description', 'calendar_today', 'schedule', 'attach_money', 'notes'
  ];

  const tabs = [
    { id: 'general', label: 'Informações Gerais', icon: 'info' },
    { id: 'connection', label: 'Conexão', icon: 'link' },
    { id: 'form', label: 'Formulário', icon: 'assignment' },
    { id: 'agents', label: 'Agentes IA', icon: 'smart_toy' },
    { id: 'settings', label: 'Configurações', icon: 'settings' }
  ];

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="edit-crm-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="header-title">
            <AppIcon name="edit_document" />
            <h2>Editar Projeto CRM</h2>
          </div>
          <button className="btn-close" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        {/* Tabs */}
        <div className="modal-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <AppIcon name={tab.icon} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="modal-content">
          {loading ? (
            <div className="loading-state">
              <AppIcon name="progress_activity" className="spinning" />
              <p>Carregando...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="tab-content">
              
              {/* ABA: Informações Gerais */}
              {activeTab === 'general' && (
                <div className="form-section">
                  <h3>Informações Básicas</h3>
                  
                  <div className="form-group">
                    <label>Nome do Projeto *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                      placeholder="Ex: CRM de Vendas"
                    />
                  </div>

                  <div className="form-group">
                    <label>Descrição</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Descreva o objetivo deste projeto..."
                      rows={3}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Ícone</label>
                      <div className="icon-selector">
                        {icons.map(icon => (
                          <button
                            key={icon}
                            type="button"
                            className={`icon-option ${formData.icon === icon ? 'selected' : ''}`}
                            onClick={() => setFormData(prev => ({ ...prev, icon }))}
                          >
                            <AppIcon name={icon} />
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Cor</label>
                      <div className="color-selector">
                        {colors.map(color => (
                          <button
                            key={color}
                            type="button"
                            className={`color-option ${formData.color === color ? 'selected' : ''}`}
                            style={{ backgroundColor: color }}
                            onClick={() => setFormData(prev => ({ ...prev, color }))}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>URL da Imagem de Fundo</label>
                    <input
                      type="url"
                      value={formData.background_image}
                      onChange={(e) => setFormData(prev => ({ ...prev, background_image: e.target.value }))}
                      placeholder="https://exemplo.com/imagem.jpg"
                    />
                    {formData.background_image && (
                      <div className="image-preview">
                        <img src={formData.background_image} alt="Preview" onError={(e) => e.target.style.display = 'none'} />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ABA: Conexão */}
              {activeTab === 'connection' && (
                <div className="form-section">
                  <h3>Conexão WhatsApp</h3>
                  
                  <div className="form-group">
                    <label>Selecione a Conexão</label>
                    <select
                      value={formData.connection_id}
                      onChange={(e) => handleConnectionChange(e.target.value)}
                    >
                      <option value="">Nenhuma conexão selecionada</option>
                      {connections.map(conn => (
                        <option key={conn.id} value={conn.id}>
                          {conn.name} - {conn.number || conn.type}
                        </option>
                      ))}
                    </select>
                    <small>Conexão para receber leads automaticamente no CRM</small>
                  </div>

                  {/* Info da Conexão Selecionada */}
                  {selectedConnection && (
                    <div className="connection-info-card">
                      <div className="connection-header">
                        <AppIcon name="check_circle" className="status-icon" />
                        <h4>Conexão Vinculada</h4>
                      </div>
                      <div className="connection-details">
                        <div className="detail-item">
                          <AppIcon name="call" />
                          <span>{selectedConnection.name}</span>
                        </div>
                        <div className="detail-item">
                          <AppIcon name="phone" />
                          <span>{selectedConnection.number || 'Não informado'}</span>
                        </div>
                        <div className="detail-item">
                          <AppIcon name="dns" />
                          <span>{selectedConnection.type || 'WhatsApp'}</span>
                        </div>
                        <div className="detail-item status">
                          <AppIcon name="wifi" />
                          <span className={`status-text ${selectedConnection.status === 'CONECTADO' ? 'connected' : ''}`}>
                            {selectedConnection.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Grupos da Conexão */}
                  {formData.connection_id && (
                    <div className="form-group">
                      <label>Grupos da Conexão</label>
                      <button 
                        type="button" 
                        className="btn-load-groups"
                        onClick={loadConnectionGroups}
                        disabled={loadingGroups}
                      >
                        {loadingGroups ? (
                          <>
                            <AppIcon name="progress_activity" className="spinning" />
                            Carregando...
                          </>
                        ) : (
                          <>
                            <AppIcon name="group" />
                            Carregar Grupos
                          </>
                        )}
                      </button>
                      
                      {connectionGroups.length > 0 && (
                        <div className="groups-list">
                          {connectionGroups.map(group => (
                            <div key={group.jid} className="group-item">
                              <AppIcon name="groups" />
                              <span>{group.name || group.jid}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ABA: Formulário */}
              {activeTab === 'form' && (
                <div className="form-section">
                  <h3>Formulário de Cadastro</h3>
                  
                  <div className="form-toggle">
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={formData.useForm}
                        onChange={(e) => setFormData(prev => ({ ...prev, useForm: e.target.checked }))}
                      />
                      <span className="toggle-slider"></span>
                      <span className="toggle-text">Ativar formulário para novos leads</span>
                    </label>
                    <small>Quando ativado, leads serão movidos para "Aguardando Formulário" até preencher os dados</small>
                  </div>

                  {formData.useForm && (
                    <div className="form-fields-section">
                      <div className="fields-header">
                        <h4>Campos do Formulário</h4>
                        <button type="button" className="btn-add-field" onClick={addFormField}>
                          <AppIcon name="add" />
                          Adicionar Campo
                        </button>
                      </div>

                      {formData.formFields.length === 0 ? (
                        <div className="no-fields">
                          <AppIcon name="assignment" />
                          <p>Nenhum campo configurado. Clique em "Adicionar Campo" para começar.</p>
                        </div>
                      ) : (
                        <div className="fields-list">
                          {formData.formFields.map((field, index) => (
                            <div key={field.id} className="field-item">
                              <div className="field-header">
                                <span className="field-number">{index + 1}</span>
                                <button 
                                  type="button" 
                                  className="btn-remove-field"
                                  onClick={() => removeFormField(field.id)}
                                >
                                  <AppIcon name="delete" />
                                </button>
                              </div>
                              
                              <div className="field-body">
                                <div className="field-row">
                                  <div className="field-input">
                                    <label>Nome do Campo</label>
                                    <input
                                      type="text"
                                      value={field.name}
                                      onChange={(e) => updateFormField(field.id, { name: e.target.value })}
                                      placeholder="Ex: Nome Completo"
                                    />
                                  </div>
                                  
                                  <div className="field-input icon-select">
                                    <label>Ícone</label>
                                    <select
                                      value={field.icon}
                                      onChange={(e) => updateFormField(field.id, { icon: e.target.value })}
                                    >
                                      {formFieldIcons.map(icon => (
                                        <option key={icon} value={icon}>{icon}</option>
                                      ))}
                                    </select>
                                  </div>
                                  
                                  <div className="field-input checkbox-field">
                                    <label className="checkbox-label">
                                      <input
                                        type="checkbox"
                                        checked={field.required}
                                        onChange={(e) => updateFormField(field.id, { required: e.target.checked })}
                                      />
                                      <span>Obrigatório</span>
                                    </label>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ABA: Agentes IA */}
              {activeTab === 'agents' && (
                <div className="form-section">
                  <h3>Agentes de IA</h3>
                  
                  {!formData.connection_id ? (
                    <div className="warning-message">
                      <AppIcon name="warning" />
                      <p>Selecione uma conexão na aba "Conexão" para vincular agentes.</p>
                    </div>
                  ) : (
                    <>
                      <p className="section-description">
                        Selecione os agentes que atenderão automaticamente os leads desta conexão.
                        Apenas agentes vinculados à mesma conexão ou sem conexão podem ser selecionados.
                      </p>

                      <div className="agents-list">
                        {agents.length === 0 ? (
                          <div className="no-agents">
                            <AppIcon name="smart_toy" />
                            <p>Nenhum agente cadastrado.</p>
                          </div>
                        ) : (
                          agents.map(agent => {
                            const isSelected = formData.agent_ids.includes(agent.id);
                            const isSameConnection = !agent.conexao_id || agent.conexao_id === formData.connection_id;
                            const isDisabled = !isSameConnection;
                            
                            return (
                              <div 
                                key={agent.id} 
                                className={`agent-item ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                onClick={() => !isDisabled && handleAgentToggle(agent.id)}
                              >
                                <div className="agent-checkbox">
                                  <AppIcon name={isSelected ? 'check_box' : 'check_box_outline_blank'} />
                                </div>
                                <div className="agent-info">
                                  <div className="agent-name">
                                    <AppIcon name="smart_toy" />
                                    {agent.nome}
                                  </div>
                                  <div className="agent-details">
                                    {agent.conexao_id ? (
                                      <span className="agent-connection">
                                        <AppIcon name="link" />
                                        Vinculado a uma conexão
                                      </span>
                                    ) : (
                                      <span className="agent-no-connection">
                                        <AppIcon name="link_off" />
                                        Sem conexão vinculada
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {isDisabled && (
                                  <div className="agent-disabled-badge">
                                    Outra conexão
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ABA: Configurações */}
              {activeTab === 'settings' && (
                <div className="form-section">
                  <h3>Configurações Gerais</h3>
                  
                  <div className="settings-grid">
                    <label className="setting-toggle">
                      <input
                        type="checkbox"
                        checked={formData.allow_comments}
                        onChange={(e) => setFormData(prev => ({ ...prev, allow_comments: e.target.checked }))}
                      />
                      <span className="toggle-slider"></span>
                      <div className="setting-info">
                        <span className="setting-title">Permitir Comentários</span>
                        <span className="setting-desc">Usuários podem adicionar comentários nos cards</span>
                      </div>
                    </label>

                    <label className="setting-toggle">
                      <input
                        type="checkbox"
                        checked={formData.allow_attachments}
                        onChange={(e) => setFormData(prev => ({ ...prev, allow_attachments: e.target.checked }))}
                      />
                      <span className="toggle-slider"></span>
                      <div className="setting-info">
                        <span className="setting-title">Permitir Anexos</span>
                        <span className="setting-desc">Usuários podem adicionar arquivos nos cards</span>
                      </div>
                    </label>

                    <label className="setting-toggle">
                      <input
                        type="checkbox"
                        checked={formData.allow_subtasks}
                        onChange={(e) => setFormData(prev => ({ ...prev, allow_subtasks: e.target.checked }))}
                      />
                      <span className="toggle-slider"></span>
                      <div className="setting-info">
                        <span className="setting-title">Permitir Subtarefas</span>
                        <span className="setting-desc">Cards podem ter subtarefas</span>
                      </div>
                    </label>

                    <label className="setting-toggle">
                      <input
                        type="checkbox"
                        checked={formData.require_due_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, require_due_date: e.target.checked }))}
                      />
                      <span className="toggle-slider"></span>
                      <div className="setting-info">
                        <span className="setting-title">Exigir Data de Entrega</span>
                        <span className="setting-desc">Todos os cards devem ter uma data de entrega</span>
                      </div>
                    </label>

                    <label className="setting-toggle">
                      <input
                        type="checkbox"
                        checked={formData.notifications_enabled}
                        onChange={(e) => setFormData(prev => ({ ...prev, notifications_enabled: e.target.checked }))}
                      />
                      <span className="toggle-slider"></span>
                      <div className="setting-info">
                        <span className="setting-title">Notificações</span>
                        <span className="setting-desc">Receber notificações de alterações no projeto</span>
                      </div>
                    </label>

                    <label className="setting-toggle">
                      <input
                        type="checkbox"
                        checked={formData.auto_archive_completed}
                        onChange={(e) => setFormData(prev => ({ ...prev, auto_archive_completed: e.target.checked }))}
                      />
                      <span className="toggle-slider"></span>
                      <div className="setting-info">
                        <span className="setting-title">Arquivar Automaticamente</span>
                        <span className="setting-desc">Cards concluídos são arquivados automaticamente</span>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Footer com botões */}
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={onClose}>
                  <AppIcon name="close" />
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <AppIcon name="progress_activity" className="spinning" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <AppIcon name="save" />
                      Salvar Alterações
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
